/**
 * Which screen a `/calls/...` path is asking for.
 *
 * The calls desk has four sub-routes that are not a call - the do-not-call
 * register, the inbound blocklist, the checklist coverage report and the cost
 * report - and every one of them has to be taken out of the call id before
 * anything reads it. Left in, "cost" is fetched as a call id, 404s, and leaves
 * the pane showing the empty state of a call that never existed.
 *
 * Lifted out of `CallsSection` when the third one arrived. With two it was a
 * pair of booleans and a `doNotCall || coverage` in four places; with three that
 * chain appears in the section, in the rail, in the layout class and in the
 * mobile view attribute, and the day somebody adds a fourth is the day one of
 * them is missed. That day came: the blocklist is the fourth, and it cost one
 * line here. One value, one place, and a test that says what each path means.
 */
/**
 * The segments themselves live HERE rather than in each folder's `index.ts`,
 * and the folders re-export them. The other way round, this file imports four
 * barrels that each export a React component, which drags the whole component
 * tree - and a stylesheet - into what is otherwise a pure string function. A
 * rule about URLs should be testable without a DOM.
 */
export const DO_NOT_CALL_SEGMENT = "do-not-call";
/**
 * Deliberately NOT a segment under `do-not-call`. The two lists look alike from
 * a distance and are opposites: that one records somebody else asking US to
 * stop, this one records US refusing THEM. A shared prefix would put them in
 * one place in the browser's history and in everybody's head.
 */
export const BLOCKLIST_SEGMENT = "blocklist";
export const COVERAGE_SEGMENT = "coverage";
export const COST_SEGMENT = "cost";

export const DO_NOT_CALL_PATH = `/calls/${DO_NOT_CALL_SEGMENT}`;
export const BLOCKLIST_PATH = `/calls/${BLOCKLIST_SEGMENT}`;
export const COVERAGE_PATH = `/calls/${COVERAGE_SEGMENT}`;
export const COST_PATH = `/calls/${COST_SEGMENT}`;

/** A screen on the calls desk that is not the call log. */
export type CallReport = "do-not-call" | "blocklist" | "coverage" | "cost";

const SEGMENTS: ReadonlyArray<readonly [string, CallReport]> = [
  [DO_NOT_CALL_SEGMENT, "do-not-call"],
  [BLOCKLIST_SEGMENT, "blocklist"],
  [COVERAGE_SEGMENT, "coverage"],
  [COST_SEGMENT, "cost"],
];

export interface CallRoute {
  /** The report being shown, or null when this is the log. */
  report: CallReport | null;
  /** The call to open in the pane. Null on every report route. */
  openId: string | null;
}

/**
 * Read a pathname into "which report" and "which call".
 *
 * Exactly one of the two is ever set. A report is never a call id and a call id
 * is never a report, which is the invariant the whole section leans on.
 */
export function readCallRoute(pathname: string): CallRoute {
  const prefix = "/calls/";
  const tail = pathname.startsWith(prefix)
    ? (pathname.slice(prefix.length).split("/")[0] || null)
    : null;
  if (!tail) return { report: null, openId: null };
  const match = SEGMENTS.find(([segment]) => segment === tail);
  return match ? { report: match[1], openId: null } : { report: null, openId: tail };
}
